import { updateSpanName } from '@sentry/angular';
import type { Span } from '@sentry/angular';
import type { SpanAttributes, WebFetchHeaders } from '@sentry/core';

export const CLIENT_REQUEST_ID_HEADER = 'x-client-request-id';

export interface HttpSpanCorrelation {
  spanId: string;
  traceId: string;
}

export interface OwnedRequestSpanMetadata {
  attributes: SpanAttributes;
  name: string;
}

/**
 * Connects Angular requests to the HTTP spans created by BrowserTracing.
 * Entries are short-lived and bounded so abandoned requests cannot grow memory use.
 */
export class HttpSpanRegistry {
  private readonly capturedRequestIds = new Map<string, number>();
  private readonly correlations = new Map<string, HttpSpanCorrelation & { expiresAt: number }>();
  private readonly ownedRequests = new Map<string, OwnedRequestSpanMetadata & { expiresAt: number }>();
  private readonly registeredOwnedSpans = new WeakSet<Span>();

  constructor(
    private readonly ttlMs = 60_000,
    private readonly maximumSize = 250,
    private readonly now = () => Date.now(),
  ) {}

  beginOwnedRequest(requestId: string, metadata: OwnedRequestSpanMetadata): void {
    this.prune();
    this.ownedRequests.set(requestId, {
      ...metadata,
      expiresAt: this.now() + this.ttlMs,
    });
    this.trim(this.ownedRequests);
  }

  registerAutomaticSpan(requestId: string | null, span: Span): void {
    this.prune();
    if (!requestId) {
      return;
    }

    const ownedRequest = this.ownedRequests.get(requestId);
    if (!ownedRequest) {
      return;
    }

    updateSpanName(span, ownedRequest.name);
    span.setAttributes(ownedRequest.attributes);
    const { spanId, traceId } = span.spanContext();
    this.correlations.set(requestId, {
      expiresAt: this.now() + this.ttlMs,
      spanId,
      traceId,
    });
    this.registeredOwnedSpans.add(span);
    this.trim(this.correlations);
  }

  enrichAutomaticSpan(span: Span, headers: WebFetchHeaders | undefined): void {
    if (!headers || !this.registeredOwnedSpans.has(span)) {
      return;
    }

    const attributes = getCloudFrontAttributes(headers);

    if (Object.keys(attributes).length > 0) {
      span.setAttributes(attributes);
    }
  }

  getCorrelation(requestId: string): HttpSpanCorrelation | undefined {
    this.prune();
    const correlation = this.correlations.get(requestId);
    if (!correlation) {
      return undefined;
    }

    return {
      spanId: correlation.spanId,
      traceId: correlation.traceId,
    };
  }

  markErrorCaptured(requestId: string): boolean {
    this.prune();
    if (this.capturedRequestIds.has(requestId)) {
      return false;
    }

    this.capturedRequestIds.set(requestId, this.now() + this.ttlMs);
    this.trim(this.capturedRequestIds);
    return true;
  }

  complete(requestId: string): void {
    this.correlations.delete(requestId);
    this.ownedRequests.delete(requestId);
  }

  private prune(): void {
    const now = this.now();
    this.deleteExpired(this.capturedRequestIds, now);
    this.deleteExpiredEntries(this.correlations, now);
    this.deleteExpiredEntries(this.ownedRequests, now);
  }

  private deleteExpired(entries: Map<string, number>, now: number): void {
    for (const [requestId, expiresAt] of entries) {
      if (expiresAt <= now) {
        entries.delete(requestId);
      }
    }
  }

  private deleteExpiredEntries<T extends { expiresAt: number }>(entries: Map<string, T>, now: number): void {
    for (const [requestId, entry] of entries) {
      if (entry.expiresAt <= now) {
        entries.delete(requestId);
      }
    }
  }

  private trim<T>(entries: Map<string, T>): void {
    while (entries.size > this.maximumSize) {
      const oldestKey = entries.keys().next().value as string | undefined;
      if (!oldestKey) {
        return;
      }
      entries.delete(oldestKey);
    }
  }
}

function getCloudFrontAttributes(headers: WebFetchHeaders): Record<string, string | number> {
  const attributes: Record<string, string | number> = {};

  const directPop = headers.get('x-amz-cf-pop')?.trim();
  const directRequestId = headers.get('x-amz-cf-id')?.trim();

  if (directPop) {
    attributes['cloudfront.pop'] = directPop;
  }

  if (directRequestId) {
    attributes['cloudfront.request_id'] = directRequestId;
  }

  switch (headers.get('x-cache')?.trim().toLowerCase()) {
    case 'hit from cloudfront':
      attributes['cloudfront.result'] = 'hit';
      break;
    case 'miss from cloudfront':
      attributes['cloudfront.result'] = 'miss';
      break;
    case 'refreshhit from cloudfront':
      attributes['cloudfront.result'] = 'refresh_hit';
      break;
    case 'error from cloudfront':
      attributes['cloudfront.result'] = 'error';
      break;
  }

  const serverTiming = headers.get('server-timing');
  if (!serverTiming) {
    return attributes;
  }

  for (const serializedMetric of serverTiming.split(',')) {
    const [rawName, ...rawParameters] = serializedMetric.split(';');
    const metricName = rawName.trim().toLowerCase();
    const { description, duration } = parseParameters(rawParameters);

    switch (metricName) {
      case 'cdn-cache-hit':
        attributes['cloudfront.cache_status'] ??= 'hit';
        break;
      case 'cdn-cache-refresh':
        attributes['cloudfront.cache_status'] ??= 'refresh';
        break;
      case 'cdn-cache-miss':
        attributes['cloudfront.cache_status'] ??= 'miss';
        break;
      case 'cdn-pop':
        if (!attributes['cloudfront.pop'] && description) {
          attributes['cloudfront.pop'] = description;
        }
        break;
      case 'cdn-rid':
        if (!attributes['cloudfront.request_id'] && description) {
          attributes['cloudfront.request_id'] = description;
        }
        break;
      case 'cdn-hit-layer':
        if (description) {
          attributes['cloudfront.hit_layer'] = description;
        }
        break;
      case 'cdn-upstream-layer':
        if (description) {
          attributes['cloudfront.upstream_layer'] = description;
        }
        break;
      case 'cdn-upstream-dns':
        if (duration !== undefined) {
          attributes['cloudfront.upstream_dns_ms'] = duration;
        }
        break;
      case 'cdn-upstream-connect':
        if (duration !== undefined) {
          attributes['cloudfront.upstream_connect_ms'] = duration;
        }
        break;
      case 'cdn-upstream-fbl':
        if (duration !== undefined) {
          attributes['cloudfront.upstream_first_byte_ms'] = duration;
        }
        break;
      case 'cdn-downstream-fbl':
        if (duration !== undefined) {
          attributes['cloudfront.downstream_first_byte_ms'] = duration;
        }
        break;
    }
  }

  return attributes;
}

function parseParameters(serializedParameters: string[]): {
  description?: string;
  duration?: number;
} {
  let description: string | undefined;
  let duration: number | undefined;

  for (const serializedParameter of serializedParameters) {
    const [rawName, ...rawValueParts] = serializedParameter.split('=');
    if (rawValueParts.length === 0) {
      continue;
    }

    const name = rawName.trim().toLowerCase();
    const rawValue = rawValueParts.join('=').trim();

    if (name === 'desc') {
      const unquoted = rawValue.startsWith('"') && rawValue.endsWith('"') ? rawValue.slice(1, -1) : rawValue;

      description = unquoted.trim() || undefined;
    }

    if (name === 'dur' && rawValue !== '') {
      const parsed = Number(rawValue);

      if (Number.isFinite(parsed) && parsed >= 0) {
        duration = parsed;
      }
    }
  }

  return { description, duration };
}

export const httpSpanRegistry = new HttpSpanRegistry();
